/**
 * Data layer for `offer-preview-api` (FS-237). Thin typed fetch wrappers over
 * /api/v1/offer-preview-api. Raw `fetch()` is confined to this file per the
 * dash contract (D6).
 *
 * Surfaces consumed by the dash:
 *   - GET    /api/v1/offer-preview-api/keys              list masked partner keys
 *   - POST   /api/v1/offer-preview-api/keys              mint a new partner key (raw shown ONCE)
 *   - POST   /api/v1/offer-preview-api/keys/:id/revoke   soft-revoke
 *
 * All requests attach `x-tenant-id: t1` so the page works against local
 * staging without an auth layer.
 */

export interface PartnerKeyMasked {
	id: string;
	tenant_id: string;
	key_prefix: string;
	scopes: string[];
	label: string | null;
	last_used_at: number | null;
	revoked_at: number | null;
	created_at: number;
}

export interface ListPartnerKeysResponse {
	keys: PartnerKeyMasked[];
}

export interface CreatePartnerKeyBody {
	label?: string | null;
	scopes: string[];
}

export interface CreatePartnerKeyResponse {
	key: PartnerKeyMasked;
	raw: string;
}

export interface RevokePartnerKeyResponse {
	revoked: true;
}

export type KeyStatus = "active" | "revoked";

const BASE = "/api/v1/offer-preview-api";
const TENANT_ID = "t1";

function baseHeaders(extra?: Record<string, string>): HeadersInit {
	return {
		accept: "application/json",
		"x-tenant-id": TENANT_ID,
		...(extra ?? {}),
	};
}

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function listKeys(): Promise<ListPartnerKeysResponse> {
	const res = await fetch(`${BASE}/keys`, {
		method: "GET",
		credentials: "include",
		headers: baseHeaders(),
	});
	return parseOrThrow<ListPartnerKeysResponse>(res);
}

export async function createKey(
	body: CreatePartnerKeyBody,
): Promise<CreatePartnerKeyResponse> {
	const res = await fetch(`${BASE}/keys`, {
		method: "POST",
		credentials: "include",
		headers: baseHeaders({ "content-type": "application/json" }),
		body: JSON.stringify(body),
	});
	return parseOrThrow<CreatePartnerKeyResponse>(res);
}

export async function revokeKey(id: string): Promise<RevokePartnerKeyResponse> {
	const res = await fetch(
		`${BASE}/keys/${encodeURIComponent(id)}/revoke`,
		{
			method: "POST",
			credentials: "include",
			headers: baseHeaders(),
		},
	);
	return parseOrThrow<RevokePartnerKeyResponse>(res);
}

// ---------------------------------------------------------------------------
// Pure helpers (exported for tests + page consumption)
// ---------------------------------------------------------------------------

export const AVAILABLE_SCOPES: readonly string[] = [
	"offers:preview",
];

export const DEFAULT_SCOPE = "offers:preview";

export function keyStatus(key: PartnerKeyMasked): KeyStatus {
	return key.revoked_at === null || key.revoked_at === undefined
		? "active"
		: "revoked";
}

export function formatUnixSec(sec: number | null | undefined): string {
	if (sec === null || sec === undefined) return "—";
	const d = new Date(sec * 1000);
	if (Number.isNaN(d.getTime())) return "—";
	const y = d.getUTCFullYear();
	const m = String(d.getUTCMonth() + 1).padStart(2, "0");
	const day = String(d.getUTCDate()).padStart(2, "0");
	const hh = String(d.getUTCHours()).padStart(2, "0");
	const mm = String(d.getUTCMinutes()).padStart(2, "0");
	return `${y}-${m}-${day} ${hh}:${mm} UTC`;
}

export function formatLastUsed(
	sec: number | null | undefined,
	nowSec: number = Math.floor(Date.now() / 1000),
): string {
	if (sec === null || sec === undefined) return "Never used";
	const delta = Math.max(0, nowSec - sec);
	if (delta < 60) return "Just now";
	if (delta < 3600) return `${Math.floor(delta / 60)}m ago`;
	if (delta < 86400) return `${Math.floor(delta / 3600)}h ago`;
	if (delta < 2_592_000) return `${Math.floor(delta / 86400)}d ago`;
	return formatUnixSec(sec);
}

export function maskToken(prefix: string): string {
	const p = typeof prefix === "string" ? prefix : "";
	return `${p}................................`;
}

export function filterKeys(
	rows: readonly PartnerKeyMasked[],
	search: string,
	status: "all" | KeyStatus,
): PartnerKeyMasked[] {
	const q = search.trim().toLowerCase();
	return rows.filter((row) => {
		if (status !== "all" && keyStatus(row) !== status) return false;
		if (q.length === 0) return true;
		const hay = `${row.label ?? ""} ${row.key_prefix} ${row.id}`.toLowerCase();
		return hay.includes(q);
	});
}

export const offerPreviewApiQueryKeys = {
	all: ["offer-preview-api"] as const,
	list: () => [...offerPreviewApiQueryKeys.all, "keys"] as const,
	detail: (id: string) => [...offerPreviewApiQueryKeys.all, "keys", id] as const,
};
